import { Helmet } from 'react-helmet'
import { nameInlineScript } from 'client/lib/inline-script'

const titleCopy = 'AI for Research | Scite'
const descriptionCopy =
  'Scite searches 300M+ scholarly articles to give you citation-backed answers and show you how every claim is supported or disputed.'

const wafEnabled = Boolean(CONFIG.awsWaf?.enabled && CONFIG.awsWaf?.sdkUrl)

//
// react-helmet emits inline script content raw (it sets innerHTML), so unlike a
// normal React text child the JSON is NOT entity-escaped — a `</script>` in the
// data would close the tag early and anything after it becomes live markup.
// tokenDomainList is operator-supplied rather than user-supplied, but it is
// env-overridable, so escape the two characters that can break out.
//
const scriptSafeJson = value => JSON.stringify(value).replace(/</g, '\\u003c').replace(/>/g, '\\u003e')

//
// Origin of the AWS WAF SDK, derived rather than hardcoded so the host isn't
// duplicated between the preconnect and the script src.
//
const wafOrigin = (() => {
  if (!wafEnabled) return null
  try {
    return new URL(CONFIG.awsWaf.sdkUrl).origin
  } catch {
    return null
  }
})()

//
// MILO:
//       Generic <head/> we render on all pages, can be overridden/added to
//       easily further down the tree with the Helmet component
//
export const Head = () => (
  <Helmet>
    <meta name='viewport' content='width=device-width, initial-scale=1, maximum-scale=5, viewport-fit=cover' />

    {CONFIG.cdnURL && <link rel='preconnect' href={CONFIG.cdnURL} crossOrigin />}
    {/* No crossorigin: the SDK below is a classic script, fetched in no-CORS
        mode, and a CORS preconnect would warm a connection it can't reuse. */}
    {wafOrigin && <link rel='preconnect' href={wafOrigin} />}
    <link rel='preconnect' href='https://fonts.googleapis.com' />
    <link rel='preconnect' href='https://fonts.gstatic.com' crossOrigin />

    <title>{titleCopy}</title>
    {CONFIG.sciteFrontendURL === 'https://staging.scite.ai' && <meta name='robots' content='noindex' />}

    <meta property='og:site_name' content='scite.ai' />
    <meta property='og:type' content='website' />

    <meta property='title' content={titleCopy} />
    <meta property='og:title' content={titleCopy} />
    <meta property='twitter:title' content={titleCopy} />

    <meta name='description' content={descriptionCopy} />
    <meta property='og:description' content={descriptionCopy} />
    <meta property='twitter:description' content={descriptionCopy} />

    <meta property='og:image' content={`${CONFIG.assetsBaseURL}/images/scite-opengraph.jpg`} />
    <meta property='twitter:image' content={`${CONFIG.assetsBaseURL}/images/scite-opengraph.jpg`} />

    <meta property='og:url' content='https://scite.ai' />
    <meta property='twitter:card' content='summary_large_image' />

    <meta name='twitter:site' content='@scite' />

    <link rel='shortcut icon' href={`${CONFIG.assetsBaseURL}/images/favicon.ico`} />
    <link
      rel='stylesheet preload prefetch'
      as='style'
      href={`${CONFIG.assetsBaseURL}/css/line-awesome-font-awesome.min.css?v=2`}
    />

    {/*
      AWS WAF Bot Control. The domain list must be set before the SDK runs — the
      SDK reads it at load time to scope the `aws-waf-token` cookie, which is
      what makes the token survive full page loads. Ordering holds because the
      parser reaches this inline script before the async one below can execute,
      and because react-helmet preserves order within a single Helmet instance.

      `async`, not blocking and not `defer`: a plain script would stall HTML
      parsing on every page if *.awswaf.com were slow, and the poll in
      lib/aws-waf.ts already tolerates the SDK arriving late — so nothing
      requires it to be present before the first API call.
    */}
    {wafEnabled && (
      <script>
        {nameInlineScript(
          `window.awsWafCookieDomainList = ${scriptSafeJson(CONFIG.awsWaf.tokenDomainList || [])};`,
          'aws-waf-config'
        )}
      </script>
    )}
    {wafEnabled && <script src={CONFIG.awsWaf.sdkUrl} async />}

    {/* Main app font */}
    <link
      rel='preload'
      as='style'
      href='https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@200;300;400;500;600&display=swap'
    />
    <link
      rel='stylesheet'
      as='style'
      href='https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@200;300;400;500;600&display=swap'
      media='print'
      onLoad="this.media='all'"
    />
    <noscript>
      {
        '<link rel="stylesheet" as="style" href="https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@200;300;400;500;600&display=swap">'
      }
    </noscript>
  </Helmet>
)

export default Head
